import { AIProvider } from '../provider';
import type { SlideImageSource } from './template';

/**
 * Slide pictures come from the regular AI image action (`/images`): one
 * generation per distinct keyword set, shared by every slot that shows it.
 * A failed generation rejects; the template drops that slot.
 */
export function createSlideImageSource(scope: {
  workspaceId: string;
  docId: string;
}): SlideImageSource {
  const images = new Map<string, Promise<string>>();

  const generate = async (keywords: string): Promise<string> => {
    const createImage = AIProvider.actions.createImage;
    if (!createImage) {
      throw new Error('AI image generation is not available');
    }
    const stream = await createImage({
      input: `A wide presentation slide picture illustrating: ${keywords}. No text or lettering in the picture.`,
      workspaceId: scope.workspaceId,
      docId: scope.docId,
      stream: true,
    });
    for await (const url of stream) {
      if (url) return url;
    }
    throw new Error('AI image generation returned no picture');
  };

  return keywords => {
    const key = keywords.trim();
    if (!key) {
      return Promise.reject(new Error('Slide section has no image keywords'));
    }
    let image = images.get(key);
    if (!image) {
      image = generate(key);
      images.set(key, image);
    }
    return image;
  };
}
